import { useQuery } from '@apollo/client';
import { isNil, sortBy } from 'lodash';
import { useContext, useMemo } from 'react';
import { Queries } from '@shared/api';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

export interface TagOption {
  id: string;
  value: string;
  label: string;
  backgroundColor: string;
  archived: boolean;
  fieldName: string;
  sortKey: string;
}

interface UseTagOptionsResponse {
  options: TagOption[];
  refetch(): void;
  loading: boolean;
}

function toTagOption(tag: API.TagOptionFragment): TagOption {
  return {
    id: tag.id,
    value: tag.slug,
    label: tag.label,
    backgroundColor: tag.colorName,
    archived: !isNil(tag.archivedAt),
    fieldName: tag.fieldName,
    sortKey: tag.sortKey,
  };
}

/**
 * @deprecated Use useWorkflowTagOptions or useCombinedTagOptions from @shared/util/hooks/useWorkflowTagOptions instead.
 */
export function useAllTagOptions(skip = false): UseTagOptionsResponse {
  const channel = useContext(CardChannelContext);
  const { data, loading, refetch } = useQuery<
    API.ListCardFieldStringTagsQuery,
    API.ListCardFieldStringTagsQueryVariables
  >(Queries.card.listCardFieldStringTags, {
    variables: {
      input: { channelId: channel?.id ?? -1 },
    },
    skip: skip || isNil(channel?.id),
  });

  const options = useMemo(() => {
    const tags: API.TagOptionFragment[] = data?.tags ?? [];
    return sortBy(tags, 'sortKey').map(toTagOption);
  }, [data?.tags]);

  return { options, loading, refetch };
}

/**
 * @deprecated Use useWorkflowTagOptions from @shared/util/hooks/useWorkflowTagOptions instead.
 */
export function useTagOptions(fieldName: string): UseTagOptionsResponse {
  const channel = useContext(CardChannelContext);
  const hasWorkflowTemplate = !isNil(channel?.workflowTemplateId);

  const { workflowTemplate, loading: wftLoading } = useWorkflowTemplate(
    hasWorkflowTemplate ? channel?.workflowTemplateId : undefined,
  );

  const {
    options: allOptions,
    loading: allTagsLoading,
    refetch,
  } = useAllTagOptions(hasWorkflowTemplate);

  const options = useMemo(() => {
    if (hasWorkflowTemplate) {
      const component = workflowTemplate?.components?.find(
        (c): c is API.TagComponent => isTagComponent(c) && c.id === fieldName,
      );
      return (
        component?.options?.map(
          (option): TagOption => ({
            id: option.value,
            value: option.value,
            label: option.label,
            backgroundColor: option.backgroundColor ?? '',
            archived: option.archived ?? false,
            fieldName,
            sortKey: option.value,
          }),
        ) ?? []
      );
    }
    const filtered = allOptions.filter(tag => tag.fieldName === fieldName);
    return sortBy(filtered, tag => tag.archived);
  }, [hasWorkflowTemplate, workflowTemplate, fieldName, allOptions]);

  const loading = hasWorkflowTemplate ? wftLoading : allTagsLoading;

  return { options, loading, refetch };
}
